import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Trash2, Warehouse } from "lucide-react";
import { Field } from "@/components/billing/BillingParts";

export function Godowns({ qs, godowns, onChanged }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ name: "", address: "", is_default: false });
  const start = (g) => { setEditing(g); setF(g ? { name: g.name, address: g.address || "", is_default: !!g.is_default } : { name: "", address: "", is_default: false }); setOpen(true); };
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    try { editing ? await api.patch(`/inventory/godowns/${editing.id}${qs}`, f) : await api.post(`/inventory/godowns${qs}`, f); toast.success("Saved"); setOpen(false); onChanged(); }
    catch (e) { toast.error("Failed", { description: e.response?.data?.detail || e.message }); }
  };
  const del = async (g) => {
    if (!window.confirm(`Delete godown "${g.name}"?`)) return;
    try { await api.delete(`/inventory/godowns/${g.id}${qs}`); toast.success("Deleted"); onChanged(); }
    catch (e) { toast.error("Failed", { description: e.response?.data?.detail || e.message }); }
  };
  return (
    <div className="space-y-3" data-testid="inv-godowns">
      <div className="flex"><Button data-testid="godown-new-btn" onClick={() => start(null)} className="ml-auto rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">+ New godown</Button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {godowns.map(g => (
          <article key={g.id} data-testid={`godown-card-${g.id}`} className="bg-white border border-zinc-200 p-4 flex gap-3">
            <div className="w-9 h-9 bg-zinc-900 text-white grid place-items-center shrink-0"><Warehouse size={16} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm flex items-center gap-2">{g.name}{g.is_default ? <span className="text-[9px] uppercase tracking-wider bg-zinc-100 px-1.5 py-0.5">default</span> : null}</div>
              <div className="text-xs text-zinc-500 truncate">{g.address || "No address"}</div>
              <div className="text-xs mt-2 tabular"><b>{g.item_count}</b> items · <b>{g.total_qty}</b> units</div>
            </div>
            <div className="flex flex-col gap-1">
              <button data-testid={`godown-edit-${g.id}`} onClick={() => start(g)} className="p-1.5 border border-zinc-200 hover:bg-zinc-900 hover:text-white"><Pencil size={12} /></button>
              <button data-testid={`godown-del-${g.id}`} onClick={() => del(g)} className="p-1.5 border border-zinc-200 hover:bg-rose-600 hover:text-white"><Trash2 size={12} /></button>
            </div>
          </article>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="godown-dialog">
          <DialogHeader><DialogTitle className="font-heading tracking-tight text-xl">{editing ? "Edit godown" : "New godown / store"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Field label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} tid="godown-name" />
            <Field label="Address" value={f.address} onChange={(v) => setF({ ...f, address: v })} tid="godown-address" />
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="godown-default" checked={f.is_default} onChange={(e) => setF({ ...f, is_default: e.target.checked })} className="accent-zinc-900" /> Default location for new stock & sales</label>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="godown-save" onClick={save} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const REASON_CLS = { sale: "text-rose-700", damage: "text-rose-700", transfer_out: "text-zinc-500", transfer_in: "text-zinc-500" };

export function StockMoves({ qs }) {
  const [moves, setMoves] = useState([]);
  useEffect(() => { api.get(`/inventory/moves${qs}`).then(r => setMoves(r.data)); }, [qs]);
  return (
    <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="inv-moves">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50"><tr className="text-[10px] tracking-[0.14em] uppercase text-zinc-500">{["Date", "Item", "Godown", "Reason", "Note", "Qty"].map(h => <th key={h} className={`px-3 py-2 font-semibold ${h === "Qty" ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
        <tbody>
          {moves.map(m => (
            <tr key={m.id} data-testid={`move-row-${m.id}`} className="border-t border-zinc-100">
              <td className="px-3 py-2 tabular text-xs">{m.date}</td><td className="px-3 py-2 font-medium">{m.item_name}</td><td className="px-3 py-2 text-xs">{m.godown_name}</td>
              <td className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold ${REASON_CLS[m.reason] || "text-emerald-700"}`}>{m.reason.replace("_", " ")}</td>
              <td className="px-3 py-2 text-xs text-zinc-500">{m.note}</td>
              <td className={`px-3 py-2 text-right tabular font-semibold ${m.qty < 0 ? "text-rose-700" : "text-emerald-700"}`}>{m.qty > 0 ? "+" : ""}{m.qty}</td>
            </tr>
          ))}
          {!moves.length && <tr><td colSpan={6} className="px-3 py-10 text-center text-zinc-500 text-sm">No stock movements yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
